import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './Products.module.css';
import API_BASE_URL from '../../config/api';

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const Products = () => {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(`${API_BASE_URL}/products?lang=${lang}`, {
          signal: controller.signal,
        });
        const json = await res.json();
        const items = Array.isArray(json) ? json : json.data || [];

        const mapped = items.map((item) => ({
          id: item.slug,
          title: item.title,
          shortDescription: stripHtml(item.short_description || item.shortDescription || ''),
          image: item.image_url || item.main_image_url || null,
        }));

        setProducts(mapped);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load products', error);
        }
      }
    };

    fetchProducts();

    return () => controller.abort();
  }, [i18n.language]);

  return (
    <div className={styles.productsPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('products.title')}</h1>
          <p className={styles.heroSubtitle}>{t('products.subtitle')}</p>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Products Section */}
      <section className={styles.productsSection}>
        <div className={styles.container}>
          <div className={styles.productsGrid}>
            {products.map((product) => (
              <div key={product.id} className={styles.productCard}>
                <div className={styles.imageWrapper}>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.title}
                      className={styles.productImage}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                  <div className={styles.imageOverlay}></div>
                </div>
                <div className={styles.cardContent}>
                  <h3 className={styles.productTitle}>{product.title}</h3>
                  <p className={styles.productDescription}>{product.shortDescription}</p>
                  <Link to={`/products/${product.id}`} className={styles.knowMoreBtn}>
                    <span>{t('products.knowMore')}</span>
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
